import { useState, useEffect } from 'react'
import { Parameters } from '../interfaces'
import requestAuditService from '../services/requestAuditService'
import { encode, isAddress, parse } from '../utils'
import { useDebounce } from './usehooks'

const useAbiParser = () => {
    const [abi, setAbi] = useState<string>('')
    const [parseError, setParseError] = useState<string | null>(null)
    const [abiFunctions, setAbiFunctions] = useState<{ [x: string]: any }>({})

    const onChange = (value: string) => {
        if (parseError) {
            setParseError(null)
        }
        setAbi(value)
    }

    const onParse = () => {
        try {
            if (parseError) {
                setParseError(null)
            }

            const parsedFunctions = parse(abi)

            setAbi(JSON.stringify(JSON.parse(abi), null, 2))
            setAbiFunctions(parsedFunctions)
        } catch (e: any) {
            setParseError(e?.message)
        }
    }

    const onHandleParse = (inputAbi: string) => {
        try {
            if (parseError) {
                setParseError(null)
            }
            const parsedFunctions = parse(inputAbi)
            setAbi(JSON.stringify(JSON.parse(inputAbi), null, 2))
            setAbiFunctions(parsedFunctions)
        } catch (e: any) {
            setParseError(e?.message)
        }
    }

    return {
        abi,
        onChange,
        onParse,
        onHandleParse,
        parseError,
        abiFunctions,
    }
}

const useParameters = (abiFunctions: any) => {
    const initialState = {
        type: 'constructor',
        funcName: '',
        inputs: [
            {
                type: '',
                value: '',
            },
        ],
        stateMutability: '',
    }
    const [parameters, setParameters] = useState<Parameters>(initialState)
    const onChange = (parameters: Parameters) => {
        let tmpParameters: Parameters = structuredClone(parameters)
        tmpParameters?.inputs?.map((input, index) => {
            if (input.type === 'tuple') {
                input.placeholder = abiFunctions[parameters.funcName]?.inputs[index]?.components
                    ?.map((o: any) => {
                        return o.type
                    })
                    ?.join(',')
            }
        })
        setParameters(tmpParameters)
    }
    const onReset = () => {
        setParameters(initialState)
    }
    return {
        parameters,
        onChange,
        onReset,
    }
}

export const useAbiEncoder = () => {
    const [loading, setLoading] = useState<boolean>(false)

    const [encoded, setEncoded] = useState<string>('')
    const [contractAddress, setContractAddress] = useState<string>('')
    const [fetchApi, setFetchApi] = useState<string>('')
    const [contractDatas, setContractDatas] = useState<any[]>([])
    const [networkUri, setNetworkUri] = useState<string>('')
    const [encodeErrors, setEncodeErrors] = useState<string[]>([])
    const {
        abi,
        onChange: onAbiChange,
        parseError,
        onParse,
        abiFunctions,
        onHandleParse,
    } = useAbiParser()
    const { parameters, onChange: onParametersChange, onReset } = useParameters(abiFunctions)

    const onClear = () => {
        onAbiChange('')
        onReset()
    }

    const handleParseClick = () => {
        onParse()
        onReset()
    }

    const onChange = (name: string) => (value: any) => {
        if (name === 'parameters') {
            onParametersChange(value)
        } else if (name === 'abi') {
            onAbiChange(value)
        } else if (name === 'provider') {
            setNetworkUri(value.target.value)
        } else if (name === 'scAddress') {
            setContractAddress(value.target.value)
        } else if (name === 'fetchApi') {
            value.target.value
                ? localStorage.setItem('API_URL', value.target.value)
                : localStorage.removeItem('API_URL')
            setFetchApi(value.target.value)
        } else if (name === 'selectContract') {
            setContractAddress(value.target.value)
            fetchApiData(fetchApi, value.target.value)
        }
    }

    useEffect(() => {
        setFetchApi(localStorage.getItem('API_URL') ?? '')
    }, [])

    useEffect(() => {
        if (fetchApi) {
            fetchApiData(fetchApi)
        } else {
            setContractDatas([])
        }
    }, [fetchApi])

    const fetchApiData = async (fetchApi: string, contractAddress?: string) => {
        setLoading(true)
        try {
            const contractDatas = await requestAuditService.requestFetchAbi(fetchApi)
            if (contractDatas && contractDatas.length && !contractAddress) {
                setContractDatas([
                    { label: 'None', value: '' },
                    ...contractDatas.map((data: any) => {
                        return { label: data.name, value: data.address }
                    }),
                ])
            }
            if (contractAddress) {
                const abi = contractDatas.find(
                    (o: { address: string }) => o.address === contractAddress
                )?.abi
                if (abi) {
                    onAbiChange(JSON.stringify(abi))
                    onHandleParse(JSON.stringify(abi))
                }
            }
        } catch (error) {
            console.log('🚀 ~ file: index.tsx:146 ~ fetchApiData ~ error:', error)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        const constructorType = 'constructor'
        const abiConstructor = abiFunctions[constructorType]
        if (typeof abiConstructor !== 'undefined') {
            onParametersChange({
                type: constructorType,
                funcName: '',
                inputs: abiConstructor.inputs || [],
                stateMutability: '',
            })
        }
    }, [abiFunctions])

    useEffect(() => {
        const { errors, encoded } = encode(parameters)
        setEncoded(encoded)
        setEncodeErrors(errors)
    }, [parameters, parameters.type, parameters.funcName, parameters.inputs])

    return {
        networkUri,
        contractAddress,
        encoded,
        encodeErrors,
        abi,
        onChange,
        parseError,
        onParse: handleParseClick,
        onClear,
        abiFunctions,
        parameters,
        fetchApi,
        contractDatas,
        loading,
        onHandleParse,
    }
}
